import {GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, lookup, managed, PlainObject, XH} from '@xh/hoist/core';
import {required, StoreRecord} from '@xh/hoist/data';
import {button} from '@xh/hoist/desktop/cmp/button';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {actionCol, calcActionColWidth, selectEditor} from '@xh/hoist/desktop/cmp/grid';
import {Icon} from '@xh/hoist/icon';
import {action, observable, runInAction} from '@xh/hoist/mobx';
import {countryNames} from '../../../../cmp/worldMap/WorldData';
import {escapeRegex} from '../../../../data/Utils';
import {wait} from '@xh/hoist/promise';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';
import {Security, WorldIndexQuote} from '../../../../data/DataTypes';
import {securityOptionRenderer, SecuritySelectOption} from '../../../../cmp/securityPicker/SecurityPicker';

export class WorldHeatmapWidgetModel extends HoistModel {
    @observable accessor countryIndexMap = [];
    @observable accessor countryColors: PlainObject = {};
    @observable accessor isConfigOpen = false;
    @managed gridModel = this.createGridModel();
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    private indices: Security[] = [];
    private indexQuotes: WorldIndexQuote[] = [];

    override onLinked() {
        this.markPersist('countryIndexMap', {dashViewModel: this.dashViewModel});

        this.dashViewModel.extraMenuItems.push({
            icon: Icon.gear(),
            text: 'Configure',
            actionFn: () => this.openConfig()
        });

        this.gridModel.loadData(this.countryIndexMap.map(data => ({id: XH.genId(), ...data})));

        this.applyColorChangesAsync();

        this.loadIndicesAsync();
    }

    @action
    openConfig() {
        this.isConfigOpen = true;
    }

    @action
    closeConfig() {
        this.isConfigOpen = false;
    }

    getIndexQuoteByCountry(country: string): WorldIndexQuote {
        const index = this.countryIndexMap.find(c => c.country == country)?.index;
        if (index) return this.indexQuotes.find(q => q.ticker == index);
    }

    private async loadIndicesAsync() {
        this.indices = await XH.secMasterService.searchSecuritiesAsync('^', 500);
    }

    private addNewRow() {
        const id = XH.genId();
        this.gridModel.store.addRecords({id});
        wait(100).then(() => {
            this.gridModel.beginEditAsync({
                record: id,
                colId: 'country'
            });
        });
    }

    private async applyColorChangesAsync() {
        const tickers = this.countryIndexMap.map(item => item.index);
        this.indexQuotes = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/economics/worldIndexQuotes`,
            params: {tickers},
            method: 'GET',
            ...getDefaultOptions()
        });

        runInAction(() => {
            this.countryIndexMap.forEach(item => {
                var quote = this.indexQuotes.find(q => q.ticker === item.index);
                if (quote) {
                    if (quote.priceChangePct > 0)
                        this.countryColors[item.country] = `rgb(0, ${Math.floor(56 + Math.random() * 200 * quote.priceChangePct * 100)}, 0)`;
                    else if (quote.priceChangePct < 0)
                        this.countryColors[item.country] = `rgb(${Math.floor(56 + Math.random() * 200 * quote.priceChangePct * -100)}, 0, 0)`;
                }
            });
        });
    }

    private applyCountryChanges() {
        this.countryIndexMap = this.gridModel.store.allRecords.map(r => ({
            country: r.data.country,
            index: r.data.index
        }));

        this.applyColorChangesAsync();
    }

    private getCountryOptions(): string[] {
        const existing = this.gridModel.store.allRecords.map(r => r.data.country);
        const options = countryNames.filter(name => !existing.includes(name));
        return options;
    }

    private getIndexOptions(input: string): SecuritySelectOption[] {
        input = input?.toUpperCase();
        const indices = this.indices.filter(idx => idx.ticker.toUpperCase().includes(input) || idx.name.toUpperCase().includes(input));
        const searchMask = input,
            regEx = new RegExp(escapeRegex(searchMask), 'ig'),
            replaceMask = `<span class="ticker-picker__highlight">${input.toUpperCase()}</span>`;
        const options = indices.map(sec => {
            return {
                value: sec.ticker,
                label: sec.name,
                displayTicker: sec.ticker.replace(regEx, replaceMask),
                displayName: sec.name?.replace(regEx, replaceMask),
                exchange: sec.exchange,
                currency: sec.currency,
                subType: sec.subType
            };
        });
        return options;
    }

    @action
    private commitRecord(record: StoreRecord) {
        if (record.isAdd) {
            this.gridModel.store.removeRecords(record);
            this.gridModel.store.updateData({add: [{...record.getValues(), id: record.id}]});
        } else {
            this.gridModel.store.updateData({update: [record.data]});
        }

        this.applyCountryChanges();
    }

    @action
    private revertRecord(record) {
        this.gridModel.store.revertRecords(record);
    }

    @action
    private removeRecord(record) {
        this.gridModel.store.removeRecords(record);
        this.applyCountryChanges();
    }

    private createGridModel(): GridModel {
        return new GridModel({
            store: {
                fields: [
                    {name: 'country', type: 'string', rules: [required]},
                    {name: 'index', type: 'string', rules: [required]}
                ]
            },
            clicksToEdit: 1,
            selModel: 'disabled',
            columns: [
                {
                    field: 'country',
                    headerName: 'Country',
                    flex: 1,
                    editable: true,
                    renderer: v => v || '<select country>',
                    editor: params =>
                        selectEditor({
                            ...params,
                            inputProps: {
                                options: this.getCountryOptions(),
                                enableFilter: true,
                                openMenuOnFocus: true
                            }
                        })
                },
                {
                    field: 'index',
                    editable: true,
                    width: 100,
                    editor: params =>
                        selectEditor({
                            ...params,
                            inputProps: {
                                queryFn: input => this.getIndexOptions(input),
                                enableFilter: true,
                                openMenuOnFocus: true,
                                menuWidth: 300,
                                optionRenderer: (option: SecuritySelectOption) => securityOptionRenderer(option)
                            }
                        })
                },
                {
                    ...actionCol,
                    headerName: button({
                        icon: Icon.plus(),
                        intent: 'success',
                        minimal: true,
                        disabled: this.gridModel?.store?.isNotValid,
                        onClick: () => this.addNewRow()
                    }),
                    width: calcActionColWidth(3),
                    actions: [
                        {
                            icon: Icon.check(),
                            intent: 'success',
                            displayFn: ({record}) => {
                                return {
                                    icon: record.isAdd ? Icon.add() : Icon.check(),
                                    disabled: record.isCommitted || record.isNotValid
                                };
                            },
                            actionFn: ({record}) => this.commitRecord(record)
                        },
                        {
                            icon: Icon.undo(),
                            intent: 'primary',
                            displayFn: ({record}) => ({disabled: !record.isModified}),
                            actionFn: ({record}) => this.revertRecord(record)
                        },
                        {
                            icon: Icon.delete(),
                            intent: 'danger',
                            displayFn: ({record}) => ({
                                icon: record.isAdd ? Icon.close() : Icon.delete()
                            }),
                            actionFn: ({record}) => this.removeRecord(record)
                        }
                    ]
                }
            ]
        });
    }
}
